import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { Alert } from "@material-ui/lab"
import { makeStyles } from "@material-ui/core/styles"
import ArchiveIcon from "@material-ui/icons/Archive"
import GetAppIcon from "@material-ui/icons/GetApp"
import SearchIcon from "@material-ui/icons/Search"
import { useSnackbar } from "../../../utils/notificacoes"
import React, { useEffect, useState } from "react"
import type { ErroApi, NfseResumo } from "../../../services/emissoesParaMeuCNPJ"
import { baixarArquivoNfe, baixarArquivoNfse } from "../../../services/emissoesParaMeuCNPJ"
import { EmissoesParaMeuCNPJActionsTypes } from "../context/emissoesParaMeuCNPJActions"
import { useEmissoesParaMeuCNPJContext } from "../context/useEmissoesParaMeuCNPJContext"

/**
 * Nome do prestador (nome fantasia, ou razão social) com o CNPJ embaixo. Sem nome
 * resolvido pelo backend, mostra só o CNPJ como antes.
 */
function CelulaPrestador({ nfse }: { nfse: NfseResumo }) {
    const nome = nfse.prestadorNomeFantasia || nfse.prestadorRazaoSocial
    if (!nome) return <>{nfse.prestador ?? "-"}</>
    const razaoDiferente =
        nfse.prestadorRazaoSocial && nfse.prestadorRazaoSocial !== nome
            ? nfse.prestadorRazaoSocial
            : ""
    return (
        <Tooltip title={razaoDiferente} disableHoverListener={!razaoDiferente}>
            <div>
                <div style={{ fontWeight: 500 }}>{nome}</div>
                <div style={{ fontSize: "0.8em", color: "#7f8c8d" }}>{nfse.prestador}</div>
            </div>
        </Tooltip>
    )
}

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: {
            paddingLeft: "20px",
            paddingRight: "20px",
            paddingTop: "80px",
        },
        [theme.breakpoints.down("sm")]: {
            paddingLeft: "15px",
            paddingRight: "15px",
            paddingTop: "70px",
        },
    },
    content: {
        margin: "auto",
        maxWidth: "1100px",
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        gap: theme.spacing(2),
        flexWrap: "wrap",
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "var(--cor-botao-salvar)",
        color: "white",
        boxShadow: "0 2px 8px rgba(var(--cor-botao-salvar-rgb),0.4)",
        "&:hover": { backgroundColor: "var(--cor-botao-salvar-hover)" },
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    zipButton: {
        borderRadius: 25,
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3),
    },
    chaveCell: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        maxWidth: 200,
    },
    emptyMsg: {
        color: "#7f8c8d",
        textAlign: "center",
        padding: theme.spacing(3),
    },
    tableHeader: {
        backgroundColor: "#f8f9fa",
    },
    tableHeaderCell: {
        fontWeight: 600,
        color: "#2c3e50",
        whiteSpace: "nowrap",
    },
}))

function isErroApi(v: unknown): v is ErroApi {
    return typeof v === "object" && v !== null && "retornoErro" in v
}

function triggerDownload(blob: Blob, fileName: string) {
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

export function EmissoesParaMeuCNPJContent() {
    const classes = useStyles()
    const { enqueueSnackbar } = useSnackbar()
    const { dados, dispatch, erroCarregamento, buscar, baixandoZip, handleBaixarZip } =
        useEmissoesParaMeuCNPJContext()

    const [baixandoId, setBaixandoId] = useState<string | null>(null)

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
    }, [erroCarregamento])

    const handleBuscar = async () => {
        if (!dados.dataInicial || !dados.dataFinal) {
            alertStart("Informe as datas inicial e final.", "error")
            return
        }
        if (dados.dataFinal < dados.dataInicial) {
            alertStart("A data final não pode ser menor que a data inicial.", "error")
            return
        }
        const diffMs = new Date(dados.dataFinal).getTime() - new Date(dados.dataInicial).getTime()
        const diffDias = diffMs / (1000 * 60 * 60 * 24)
        if (diffDias > 31) {
            alertStart("O período máximo permitido é de 31 dias.", "error")
            return
        }
        await buscar()
    }

    const handleBaixarPdfNfse = async (id: string) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setBaixandoId(id)
        try {
            const result = await baixarArquivoNfse(autorizacao, id)
            if (isErroApi(result)) {
                alertStart(result.mensagem || "Erro ao baixar PDF", "error")
            } else {
                triggerDownload(result, `NFSE_${id}.pdf`)
            }
        } finally {
            setBaixandoId(null)
        }
    }

    const handleBaixarNfe = async (url: string, tipo: "pdf" | "xml", id: string) => {
        const autorizacao = localStorage.getItem("chaveSistema")
        if (!autorizacao) return
        setBaixandoId(`${id}_${tipo}`)
        try {
            const result = await baixarArquivoNfe(autorizacao, url, tipo)
            if (isErroApi(result)) {
                alertStart(result.mensagem || "Erro ao baixar arquivo", "error")
            } else {
                triggerDownload(result, `NFE_${id}.${tipo}`)
            }
        } finally {
            setBaixandoId(null)
        }
    }

    const formatarValor = (valor: number | null) => {
        if (valor == null) return "-"
        return valor.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Notas emitidas para meu CNPJ
                </Typography>

                {/* Filtros */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <SearchIcon className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Filtros
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Box className={classes.filterRow}>
                            <TextField
                                label="Data inicial"
                                type="date"
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={dados.dataInicial}
                                onChange={e =>
                                    dispatch({
                                        type: EmissoesParaMeuCNPJActionsTypes.ATUALIZAR_DATA_INICIAL,
                                        payload: e.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="Data final"
                                type="date"
                                variant="outlined"
                                size="small"
                                InputLabelProps={{ shrink: true }}
                                value={dados.dataFinal}
                                onChange={e =>
                                    dispatch({
                                        type: EmissoesParaMeuCNPJActionsTypes.ATUALIZAR_DATA_FINAL,
                                        payload: e.target.value,
                                    })
                                }
                            />
                            <Button
                                variant="contained"
                                className={classes.saveButton}
                                startIcon={<SearchIcon />}
                                onClick={handleBuscar}
                            >
                                Buscar
                            </Button>
                            <Button
                                variant="outlined"
                                className={classes.zipButton}
                                startIcon={
                                    baixandoZip ? <CircularProgress size={16} /> : <ArchiveIcon />
                                }
                                onClick={handleBaixarZip}
                                disabled={baixandoZip}
                            >
                                Baixar ZIP (PDF+XML)
                            </Button>
                        </Box>
                        <Box style={{ marginTop: 8 }}>
                            {dados.notasServico.length > 0 && (
                                <Typography
                                    variant="body2"
                                    style={{ color: "#7f8c8d", display: "inline", marginRight: 16 }}
                                >
                                    <strong>Total NFS-e:</strong> {dados.notasServico.length}
                                </Typography>
                            )}
                            {dados.notasMercadoria.length > 0 && (
                                <Typography
                                    variant="body2"
                                    style={{ color: "#7f8c8d", display: "inline" }}
                                >
                                    <strong>Total NF-e:</strong> {dados.notasMercadoria.length}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </Paper>

                {/* Tabela NF-e */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Notas de Mercadoria (NF-e)
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.mensagemMercadoria && (
                            <Alert severity="info" style={{ marginBottom: 12 }}>
                                {dados.mensagemMercadoria}
                            </Alert>
                        )}
                        {dados.notasMercadoria.length === 0 ? (
                            <Typography className={classes.emptyMsg}>
                                Nenhuma NF-e encontrada. Use o filtro para buscar.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ minWidth: 970, width: "100%" }}
                                >
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 110 }}
                                            >
                                                Emissão
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 120 }}
                                            >
                                                Status
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 180 }}
                                            >
                                                Emitente
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 100 }}
                                            >
                                                Valor
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 90 }}
                                            >
                                                Nº
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 90 }}
                                            >
                                                Série
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 200 }}
                                            >
                                                Chave
                                            </TableCell>
                                            <TableCell style={{ minWidth: 80 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.notasMercadoria.map(nfe => (
                                            <TableRow key={nfe.id} hover>
                                                <TableCell>{nfe.dataEmissao ?? "-"}</TableCell>
                                                <TableCell>{nfe.status ?? "-"}</TableCell>
                                                <TableCell>{nfe.emitente ?? "-"}</TableCell>
                                                <TableCell>{formatarValor(nfe.valorNfe)}</TableCell>
                                                <TableCell>{nfe.numero ?? "-"}</TableCell>
                                                <TableCell>{nfe.serie ?? "-"}</TableCell>
                                                <TableCell
                                                    className={classes.chaveCell}
                                                    title={nfe.chave ?? ""}
                                                >
                                                    {nfe.chave ?? "-"}
                                                </TableCell>
                                                <TableCell
                                                    align="center"
                                                    style={{ whiteSpace: "nowrap" }}
                                                >
                                                    {nfe.pdf && (
                                                        <Tooltip title="Baixar PDF">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={
                                                                        baixandoId ===
                                                                        `${nfe.id}_pdf`
                                                                    }
                                                                    onClick={() =>
                                                                        handleBaixarNfe(
                                                                            nfe.pdf!,
                                                                            "pdf",
                                                                            nfe.id
                                                                        )
                                                                    }
                                                                >
                                                                    {baixandoId ===
                                                                    `${nfe.id}_pdf` ? (
                                                                        <CircularProgress
                                                                            size={16}
                                                                        />
                                                                    ) : (
                                                                        <GetAppIcon fontSize="small" />
                                                                    )}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                    {nfe.xml && (
                                                        <Tooltip title="Baixar XML">
                                                            <span>
                                                                <IconButton
                                                                    size="small"
                                                                    disabled={
                                                                        baixandoId ===
                                                                        `${nfe.id}_xml`
                                                                    }
                                                                    onClick={() =>
                                                                        handleBaixarNfe(
                                                                            nfe.xml!,
                                                                            "xml",
                                                                            nfe.id
                                                                        )
                                                                    }
                                                                >
                                                                    {baixandoId ===
                                                                    `${nfe.id}_xml` ? (
                                                                        <CircularProgress
                                                                            size={16}
                                                                        />
                                                                    ) : (
                                                                        <GetAppIcon
                                                                            fontSize="small"
                                                                            style={{
                                                                                color: "#666",
                                                                            }}
                                                                        />
                                                                    )}
                                                                </IconButton>
                                                            </span>
                                                        </Tooltip>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>

                {/* Tabela NFS-e */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Notas de Serviço (NFS-e)
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        {dados.mensagemServico && (
                            <Alert severity="info" style={{ marginBottom: 12 }}>
                                {dados.mensagemServico}
                            </Alert>
                        )}
                        {dados.notasServico.length === 0 ? (
                            <Typography className={classes.emptyMsg}>
                                Nenhuma NFS-e encontrada. Use o filtro para buscar.
                            </Typography>
                        ) : (
                            <TableContainer style={{ overflowX: "auto" }}>
                                <Table
                                    size="small"
                                    stickyHeader
                                    style={{ minWidth: 950, width: "100%" }}
                                >
                                    <TableHead className={classes.tableHeader}>
                                        <TableRow>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 110 }}
                                            >
                                                Emissão
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 120 }}
                                            >
                                                Situação
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 180 }}
                                            >
                                                Prestador
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 180 }}
                                            >
                                                Tomador
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 100 }}
                                            >
                                                Valor
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 90 }}
                                            >
                                                Nº NFS-e
                                            </TableCell>
                                            <TableCell
                                                className={classes.tableHeaderCell}
                                                style={{ minWidth: 90 }}
                                            >
                                                Série
                                            </TableCell>
                                            <TableCell style={{ minWidth: 80 }} />
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {dados.notasServico.map(nfse => (
                                            <TableRow key={nfse.id} hover>
                                                <TableCell>{nfse.emissao ?? "-"}</TableCell>
                                                <TableCell>{nfse.situacao ?? "-"}</TableCell>
                                                <TableCell>
                                                    <CelulaPrestador nfse={nfse} />
                                                </TableCell>
                                                <TableCell>{nfse.tomador ?? "-"}</TableCell>
                                                <TableCell>
                                                    {formatarValor(nfse.valorServico)}
                                                </TableCell>
                                                <TableCell>{nfse.numeroNfse ?? "-"}</TableCell>
                                                <TableCell>{nfse.serie ?? "-"}</TableCell>
                                                <TableCell align="center">
                                                    <Tooltip title="Baixar PDF">
                                                        <span>
                                                            <IconButton
                                                                size="small"
                                                                disabled={baixandoId === nfse.id}
                                                                onClick={() =>
                                                                    handleBaixarPdfNfse(nfse.id)
                                                                }
                                                            >
                                                                {baixandoId === nfse.id ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <GetAppIcon
                                                                        fontSize="small"
                                                                        style={{ color: "#4caf50" }}
                                                                    />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </Box>
                </Paper>
            </Box>
        </Box>
    )
}
